/**
 * Redaction for feedback text and captured console errors.
 *
 * Lifted verbatim from Links'/CMS's `lib/feedback/redact.ts` (byte-identical
 * between the two repos) - this runs in the browser, before anything is
 * sent. That placement is the point: the screenshot and the console ring
 * buffer both carry data the user did not consciously choose to send, so a
 * credential has to be gone before the request leaves the page rather than
 * filtered by a server we then have to trust. A silent failure here writes a
 * live token onto a Jira ticket that every Jira user can read.
 *
 * Ordering matters. Bearer/Basic headers are matched before the bare-JWT rule,
 * so `Authorization: Bearer eyJ...` redacts as a header rather than leaving a
 * dangling `Authorization:` in front of `[redacted-jwt]`.
 */

type Rule = { re: RegExp; to: string };

const RULES: Rule[] = [
  // Auth headers, value and all.
  { re: /\b(authorization\s*[:=]\s*)(bearer|basic)\s+[\w\-._~+/]+=*/gi, to: '$1[redacted]' },
  { re: /\b(bearer|basic)\s+[\w\-._~+/]{8,}=*/gi, to: '[redacted]' },

  // JSON Web Tokens: header.payload.signature, all base64url.
  { re: /\beyJ[\w-]{4,}\.[\w-]{4,}\.[\w-]{4,}/g, to: '[redacted-jwt]' },

  // Vendor-prefixed keys we actually use.
  { re: /\bATATT[\w\-=]{16,}/g, to: '[redacted-key]' }, // Atlassian
  { re: /\bxox[baprs]-[\w-]{8,}/gi, to: '[redacted-key]' }, // Slack
  { re: /\bgh[pousr]_[A-Za-z0-9]{16,}/g, to: '[redacted-key]' }, // GitHub
  { re: /\bsk-[A-Za-z0-9\-_]{16,}/g, to: '[redacted-key]' }, // OpenAI-style
  { re: /\bAIza[\w\-]{20,}/g, to: '[redacted-key]' }, // Google API

  // key=value / key: value for anything named like a credential.
  {
    re: /\b(api[_-]?key|apikey|access[_-]?token|refresh[_-]?token|token|secret|password|passwd|pwd|client[_-]?secret)(\s*["']?\s*[:=]\s*["']?)([^\s,;"'&}]{4,})/gi,
    to: '$1$2[redacted]',
  },

  // A bare `key=` / `key:`. Stricter value than the named list above: 12+ chars
  // of credential alphabet, so "the key: filter resets" survives untouched.
  { re: /\b(key)(\s*["']?\s*[:=]\s*["']?)([A-Za-z0-9\-_.+/]{12,})/gi, to: '$1$2[redacted]' },

  // Long hex runs - session ids, signing keys, raw digests. 16 is the floor
  // because a 128-bit key is 32 hex chars but a truncated one is still a key.
  { re: /\b[0-9a-f]{16,}\b/gi, to: '[redacted-hex]' },
];

/** Redact one string. Safe on any input, including empty and non-strings. */
export function redact(input: unknown): string {
  if (typeof input !== 'string' || input.length === 0) return '';
  let out = input;
  for (const { re, to } of RULES) {
    // Fresh lastIndex each pass: these are /g and get reused across calls.
    re.lastIndex = 0;
    out = out.replace(re, to);
  }
  return out;
}

export interface ConsoleEntry {
  at: string;
  kind: 'error' | 'unhandledrejection' | 'console';
  message: string;
}

/** Redact every message in a console buffer, dropping any that redact to nothing. */
export function redactEntries(entries: readonly ConsoleEntry[]): ConsoleEntry[] {
  if (!Array.isArray(entries)) return [];
  return entries.map((e) => ({ ...e, message: redact(e?.message) })).filter((e) => e.message.length > 0);
}

/**
 * Redact a route or URL, including its query string.
 *
 * Scope gap found during the intake build: a dashboard route can carry a
 * credential in its query (`/dashboard?token=…`), and the original rules only
 * ever saw `feedback` and console messages. Anything derived from the address
 * bar goes through here.
 */
export function redactRoute(route: unknown): string {
  if (typeof route !== 'string' || route.length === 0) return '';
  const [path, query] = route.split('?', 2);
  if (!query) return redact(path);
  const cleaned = query
    .split('&')
    .map((pair) => {
      const [k, ...rest] = pair.split('=');
      if (rest.length === 0) return redact(pair);
      return /^(key|token|secret|password|passwd|pwd|api[_-]?key|access[_-]?token|refresh[_-]?token|sig|signature|auth)$/i.test(k ?? '')
        ? `${k}=[redacted]`
        : `${k}=${redact(rest.join('='))}`;
    })
    .join('&');
  return `${redact(path)}?${cleaned}`;
}
